<script lang="ts" setup>
withDefaults(
  defineProps<{
    coverImage: string
    variant?: 'default' | 'with-block' | 'bottom-bar'
    inset?: boolean
    isSite?: boolean
  }>(),
  {
    variant: 'default',
    inset: false,
    isSite: false,
  },
)
defineEmits(['click'])
</script>

<template>
  <div class="cursor-pointer " @click="$emit('click')">
    <div
      v-if="variant === 'with-block' || variant === 'default'"
      class="flex flex-row cursor-pointer relative rounded-r-2xl rounded-l-3xl hover:opacity-90 active:opacity-80"
      :class="{
        'bg-[#F3F3F3] dark:bg-[#272727] ':
          variant === 'with-block' && !inset && !isSite,
        'bg-white dark:bg-[#272727]': variant === 'with-block' && inset,
        'bg-[#292A32]': isSite,
      }"
    >
      <div
        class="mask flex relative rounded-[0.875rem] overflow-hidden"
        :class="{
          'max-w-40 w-full h-43': variant === 'with-block',
          'w-50 h-50': variant !== 'with-block',
        }"
      >
        <img
          class="object-cover"
          :class="{
            'w-40 h-43': variant === 'with-block',
            'w-50 h-50': variant !== 'with-block',

          }"
          :src="coverImage"
        >
        <div class="absolute bottom-0 w-full" :class="{ 'h-full': !$slots.location || !$slots.title || !$slots.date || !$slots.time }">
          <div
            class=" flex flex-col items-start px-2.5 pb-2.5"
            :class="{ 'h-24 justify-end': variant === 'with-block', 'h-full before:(!bg-gradient-to-b ) after:( !bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,1)]) ': !$slots.location || !$slots.title || !$slots.date || !$slots.time,
            }"
          >
            <div
              v-if="variant === 'default' && $slots.location"
              class="bg-black bg-opacity-40  rounded-[1.25rem] flex items-center px-1.5 h-5 truncate max-w-full"
            >
              <div class="flex flex-row items-center truncate w-full">
                <slot name="location" />
              </div>
            </div>
            <p
              v-if="variant !== 'with-block' && $slots.title"
              class="text-lg tracking-wide line-clamp-2 m-0 text-white my-1.5 leading-tight capitalize h-11.5"
            >
              <slot name="title" />
            </p>
            <div
              v-if="variant !== 'with-block' && ($slots.date || $slots.time)"
              class="flex flex-row justify-between gap-4 text-[#979797] text-xs tracking-wide"
            >
              <div v-if="$slots.date" class="flex items-center gap-1.5">
                <Icon name="i-belong:icon-calendar" />

                <p class="m-0 uppercase">
                  <slot name="date" />
                </p>
              </div>
              <div v-if="$slots.time" class="flex items-center gap-1.5">
                <Icon name="i-belong:icon-time" class="w-3 h-3 bg-cover" />

                <p class="m-0">
                  <slot name="time" />
                </p>
              </div>
            </div>
            <slot name="additionalBlock" />
          </div>
        </div>
        <slot name="mask" />
        <div v-if="variant !== 'with-block' && $slots.more" class="absolute top-2.5 right-2 z-2">
          <slot name="more" />
        </div>
      </div>
      <div
        v-if="variant === 'with-block'"
        class="flex flex-row pt-3 pl-4 pr-2 pb-2.5 w-full line-clamp-1"
      >
        <div class="flex flex-col justify-between w-full">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row gap-3.5 text-[#979797] text-xs tracking-wide">
              <div class="flex items-center gap-1">
                <Icon name="i-belong:icon-calendar" class="min-w-3 h-3 bg-cover" />

                <p class="m-0 uppercase">
                  <slot name="date" />
                </p>
              </div>
              <div class="flex items-center gap-1">
                <Icon name="i-belong:icon-time" class="min-w-3 h-3 bg-cover" />
                <p class="m-0">
                  <slot name="time" />
                </p>
              </div>
            </div>
            <div>
              <p
                class="text-sm tracking-wide line-clamp-2 m-0 leading-tight capitalize break-words"
              >
                <slot name="title" />
              </p>
              <p
                class="line-clamp-2 mt-1.5 text-xs font-medium tracking-wide m-0 dark:text-[#959595]"
                :class="{ 'text-[#A5A6B6]': isSite }"
              >
                <slot name="description" />
              </p>
            </div>
          </div>
          <div class="flex flex-row items-center truncate max-w-full">
            <slot name="location" />
          </div>
        </div>
        <div v-if="variant === 'with-block' && $slots.more">
          <slot name="more" />
        </div>
      </div>
      <div v-if="$slots.QR && variant !== 'with-block'" class="absolute top-0 right-2">
        <slot name="QR" />
      </div>
    </div>

    <div
      v-if="variant === 'bottom-bar'"
      class="flex flex-col min-w-50 max-w-50 w-full"
    >
      <div class="flex relative">
        <img
          class="object-cover h-25 w-full rounded-t-2xl"
          :src="coverImage"
        >
        <slot name="mask" />

        <div v-if="$slots.location" class="absolute left-2.5 bottom-2 z-99 bg-[#000000] rounded-full truncate px-1.5 max-w-[calc(100%_-_20px)] py-0.5">
          <slot name="location" />
        </div>

        <div
          class="w-full h-14 absolute bottom-0"
          style="background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
"
        />
      </div>
      <div
        class="flex flex-col rounded-b-4 pt-2 pb-2.5 px-2 rounded-b-2xl"
        :class="{ 'bg-[#292A32]': isSite, 'bg-[#F1F1F4] dark:bg-[#272727]': inset, 'bg-white dark:bg-[#272727]': !inset && !isSite }"
      >
        <p class="line-clamp-2 leading-5 h-10 m-0 text-base">
          <slot name="title" />
        </p>

        <div class="flex flex-row mt-4.5 justify-between">
          <div class="flex items-center gap-1" :class="{ 'text-[#898B9F]': isSite, 'dark:text-[#DBDBDB]': !isSite }">
            <Icon
              name="i-belong:icon-calendar"
            />
            <p
              class="m-0 uppercase text-xs tracking-wide"
            >
              <slot name="date" />
            </p>
          </div>
          <div class="flex items-center gap-1" :class="{ 'text-[#898B9F]': isSite, 'dark:text-[#DBDBDB]': !isSite }">
            <Icon name="i-belong:icon-time" />
            <p
              class="m-0 uppercase text-xs tracking-wide "
            >
              <slot name="time" />
            </p>
          </div>

          <div v-if="$slots.bottom">
            <slot name="bottom" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
